/**
 * 購入側（buyer）・販売側（seller）の PHP WreckerappController::beforeRender 相当（在庫期限切れメール・更新）
 * buyer / seller 画面などから admin の postMailsubOdrdZaikoKaito API を叩く。
 *
 * 仕様・利用方法・環境変数・戻り値の詳細: `apps/admin/docs/postMailsubOdrdZaikoKaito_folder_structure.md`
 *
 * @param {object} [options]
 * @param {'buyer'|'seller'} [options.portal] - API パス `/api/{portal}/postMailsubOdrdZaikoKaito`。省略時は buyer
 * @param {string} [options.adminRtBaseUrl] - 省略時は process.env.NEXT_PUBLIC_ADMIN_RT_BASE_URL
 * @param {string} [options.apiToken] - 省略時は process.env.NEXT_PUBLIC_POST_MAILSUB_ODRD_ZAIKO_API_TOKEN（admin の API_TOKEN と同一）
 * @param {{ user?: { CorpID?: string, Email?: string } }} [options.session] - buyer のみ。user に CorpID / Email
 * @param {string} [options.corpId] - 法人 ID（seller は必須扱い。buyer では session の代わり可）
 * @param {string} [options.email] - メール（同上）
 * @param {string} [options.userId] - seller のみ必須。担当者 UserID（henkokind 用）
 * @param {string} [options.UserID] - userId の別名
 * @param {{ warn?: Function, debug?: Function }} [options.log] - 例: logjs インスタンス
 * @returns {Promise<
 *   | { ok: false; skipped: true; reason: 'no_admin_base' | 'no_api_token' | 'invalid_user' | 'invalid_user_id' }
 *   | { ok: boolean; status: number; data: unknown }
 *   | { ok: false; error: string }
 * >}
 */
export async function requestPostMailsubOdrdZaikoKaito(options = {}) {
  // 管理アプリのベース URL（環境変数 or 引数）。末尾スラッシュは除去
  const fromEnv = typeof process !== "undefined" && process.env && process.env.NEXT_PUBLIC_ADMIN_RT_BASE_URL ? process.env.NEXT_PUBLIC_ADMIN_RT_BASE_URL : "";
  const adminRtBase = String(options.adminRtBaseUrl ?? fromEnv).replace(/\/$/, "");
  if (!adminRtBase) {
    options.log?.warn?.("postMailsubOdrdZaikoKaito: NEXT_PUBLIC_ADMIN_RT_BASE_URL is not set");
    return { ok: false, skipped: true, reason: "no_admin_base" };
  }

  // 公開 API 用トークン（admin 側 API_TOKEN と同一）。未設定なら呼び出し不可
  const tokenEnv = typeof process !== "undefined" && process.env && process.env.NEXT_PUBLIC_POST_MAILSUB_ODRD_ZAIKO_API_TOKEN ? process.env.NEXT_PUBLIC_POST_MAILSUB_ODRD_ZAIKO_API_TOKEN : "";
  const apiToken = String(options.apiToken ?? tokenEnv ?? "").trim();
  if (!apiToken) {
    options.log?.warn?.("postMailsubOdrdZaikoKaito: NEXT_PUBLIC_POST_MAILSUB_ODRD_ZAIKO_API_TOKEN（または options.apiToken）が未設定です");
    return { ok: false, skipped: true, reason: "no_api_token" };
  }

  // セッション相当から法人 ID・メールを取得（直指定 options もフォールバック）
  const sessionUser = options.session?.user;
  const corpId = sessionUser?.CorpID ?? options.corpId;
  const emailRaw = sessionUser?.Email ?? options.email;
  const email = String(emailRaw ?? "").trim();

  // ゲスト・デフォルト法人・メール欠落は API に送らない
  if (!corpId || corpId === "DEFAULT_CORP" || !email) {
    options.log?.warn?.("postMailsubOdrdZaikoKaito: CorpID / Email が不正のため送信しません");
    return { ok: false, skipped: true, reason: "invalid_user" };
  }

  // buyer / seller のどちらの公開ルートに POST するか（それ以外は buyer にフォールバックし警告）
  const portalRaw = String(options.portal ?? "buyer").toLowerCase();
  const portal = portalRaw === "seller" ? "seller" : "buyer";
  if (portalRaw !== "buyer" && portalRaw !== "seller") {
    options.log?.warn?.("postMailsubOdrdZaikoKaito: portal は buyer または seller のみ有効です（不正値は buyer として扱います）", {
      portal: options.portal,
    });
  }

  const userIdSeller = String(options.userId ?? options.UserID ?? "").trim();
  if (portal === "seller" && !userIdSeller) {
    options.log?.warn?.("postMailsubOdrdZaikoKaito: seller では userId（または UserID）が必須のため送信しません");
    return { ok: false, skipped: true, reason: "invalid_user_id" };
  }

  // エンドポイント URL とリクエストボディ（buyer: 従来の session 形、seller: ボディ直下のみ）
  const zaikoKaitoUrl = `${adminRtBase}/${portal}/postMailsubOdrdZaikoKaito`;
  const bodyObj =
    portal === "seller"
      ? { corpId, email, userId: userIdSeller }
      : {
          session: {
            user: {
              CorpID: corpId,
              Email: email,
            },
          },
        };
  const body = JSON.stringify(bodyObj);

  options.log?.debug?.("postMailsubOdrdZaikoKaito: POST", {
    url: zaikoKaitoUrl,
    body: bodyObj,
  });

  // POST（Cookie 同送 + X-API-Token で認証）。ネットワークエラー等は warn のみで握りつぶす
  try {
    const res = await fetch(zaikoKaitoUrl, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-API-Token": apiToken,
      },
      body,
    });
    const data = await res.json().catch(() => ({}));
    options.log?.debug?.("postMailsubOdrdZaikoKaito", { status: res.status, data });
    return { ok: res.ok, status: res.status, data };
  } catch (err) {
    options.log?.warn?.("postMailsubOdrdZaikoKaito", err);
    return { ok: false, error: err?.message ?? String(err) };
  }
}
